import { useCallback, useState } from 'react';
import type { GamePlayerProjectionTrading } from '@library/types/trading';

export type RemovalWarningType = 'Tacos' | 'Community Plays';

export function useRemovalWarnings({
  changes,
  projections,
  onNoWarnings
}: {
  changes: Array<Partial<GamePlayerProjectionTrading>>;
  projections: Array<GamePlayerProjectionTrading> | undefined;
  onNoWarnings: () => void | undefined;
}) {
  const [removalWarnings, setRemovalWarnings] = useState<
    Record<RemovalWarningType, number> | undefined
  >();

  const processWarnings = useCallback(() => {
    if (!projections) return;

    const warnings: Record<string, number> = {};
    function addWarning(name: RemovalWarningType) {
      warnings[name] = (warnings[name] ?? 0) + 1;
    }

    changes.forEach((change) => {
      if (change.status !== 'removed') return;

      const projection = projections.find(
        (proj) => proj.projectionId === change.projectionId
      );
      if (!projection) return;

      if (projection.isCommunityPlay) {
        addWarning('Community Plays');
      }
      if (projection.isPromo && !projection.optIn) {
        addWarning('Tacos');
      }
    });

    if (Object.keys(warnings).length > 0) {
      setRemovalWarnings(warnings);
    } else {
      onNoWarnings?.();
    }
  }, [changes, onNoWarnings, projections]);

  const handleCancelRemoval = useCallback(() => {
    setRemovalWarnings(undefined);
  }, []);

  const handleConfirmRemoval = useCallback(() => {
    setRemovalWarnings(undefined);
    onNoWarnings?.();
  }, [onNoWarnings]);

  return {
    processWarnings,
    removalWarnings,
    handleCancelRemoval,
    handleConfirmRemoval
  };
}
